<template>
  <div class="loader-container" :class="{ shrink: isArrow }">
    <div
      class="loader-content"
      :class="{
        'move-right': moveRight,
        'move-left': moveLeft,
        'center-ball': isArrow,
        [`counter-${currentNumber}`]: true,
      }"
    >
      <div v-if="props.kind === 'counter'">
        <span
          v-if="!isArrow"
          ref="ballContentElement"
          class="slide-left-animation number"
          >{{ currentNumber }}</span
        >
        <img v-else width="38" height="38" src="/images/arrow-right.svg" />
      </div>

      <div v-if="props.kind === 'indeterminate'">
        <img
          ref="ballContentElement"
          class="slide-left-animation"
          width="38"
          height="38"
          :src="`/images/${possibleIcons[currentIconIndex]}`"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';

const props = defineProps<
  | {
      kind: 'counter';
      count: number;
    }
  | { kind: 'indeterminate' }
>();

const currentNumber = ref(props.kind === 'counter' ? props.count : 0);

const possibleIcons = [
  'book-open-white.svg',
  'globe.svg',
  'message-white.svg',
  'plane.svg',
  'microphone-white.svg',
  'note.svg',
];
const currentIconIndex = ref(0);

const isArrow = ref(false);
const moveRight = ref(true);
const moveLeft = ref(false);

const ballContentElement = ref<HTMLElement | null>(null);

const emit = defineEmits(['onFinish']);

function startLoader() {
  const interval = setInterval(() => {
    if (props.kind === 'counter') {
      if (currentNumber.value > 1) {
        currentNumber.value--;
        moveBall();
        setTimeout(playAudio, 50);
      } else {
        clearInterval(interval);
        moveRight.value = false;
        moveLeft.value = false;
        isArrow.value = true;
        setTimeout(() => emit('onFinish'), 1200);
      }
    }

    if (props.kind === 'indeterminate') {
      if (currentIconIndex.value >= possibleIcons.length - 1) {
        currentIconIndex.value = 0;
      } else {
        currentIconIndex.value++;
      }

      moveBall();
    }
  }, 1200);
}

function moveBall() {
  moveRight.value = !moveRight.value;
  moveLeft.value = !moveLeft.value;

  if (ballContentElement.value) {
    retriggerSlideLeftAnimation(ballContentElement.value);
  }
}

function playAudio() {
  const audio = new Audio('/audio/water-drip.mp3');
  audio.play();
  audio.remove();
}

function retriggerSlideLeftAnimation(element: HTMLElement) {
  element.classList.remove('slide-left-animation');
  void element.offsetWidth; // Trigger reflow
  element.classList.add('slide-left-animation');
}

onMounted(() => {
  if (props.kind === 'counter') {
    setTimeout(playAudio, 200);
  }

  setTimeout(startLoader, 240);
});
</script>

<style scoped>
.loader-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 0;
  height: 0;
  background-color: var(--color-brand-02);
  border-radius: 50%;
  position: relative;
  overflow: hidden;
  animation: grow 0.4s forwards;
  flex: none;
}

@keyframes grow {
  from {
    width: 0;
    height: 0;
    border-radius: 50%;
  }
  to {
    width: 200px;
    height: 90px;
    border-radius: 90px;
  }
}

.loader-content {
  width: 79px;
  height: 79px;
  background-color: var(--color-brand-03);
  border-radius: 50%;
  color: var(--color-neutral-01);
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: -24px;
  transition: transform 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55);
}

.loader-content.counter-1 {
  background-color: var(--color-brand-05);
}

.loader-content.counter-2 {
  background-color: var(--color-brand-04);
}

.move-right {
  transform: translateX(140px); /* Adjust padding right */
}

.move-left {
  transform: translateX(28px); /* Move back to the left edge */
}

.center-ball {
  left: 50%;
  transform: translateX(-50%); /* Center the ball */
  background-color: var(--color-brand-03) !important;
}

.shrink {
  width: 120px; /* Slightly larger than the ball */
  height: 120px;
  border-radius: 120px; /* Ensures a circle */
}

.loader-container.shrink {
  animation: shrink 0.5s forwards;
}

@keyframes shrink {
  from {
    width: 200px;
    height: 90px;
    border-radius: 90px;
  }
  to {
    width: 90px;
    height: 90px;
    border-radius: 90px;
  }
}

.number {
  font-size: 38px;
  font-weight: 700;
}

.slide-left-animation {
  animation: slide-left 0.34s forwards;
}

@keyframes slide-left {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>
